import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { usePushNotifications } from "@/hooks/use-push-notifications";
import { isNativePushPlatform } from "@/lib/native-push";
import { useEffect, useState } from "react";

function EnablePushNotificationsNative() {
  const {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe,
  } = usePushNotifications();

  const [uiChecked, setUiChecked] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  // Sync UI with actual subscription state
  useEffect(() => {
    if (!isToggling) {
      setUiChecked(isSubscribed);
    }
  }, [isSubscribed, isToggling]);

  // Auto-subscribe on mount if permission already granted and not subscribed
  useEffect(() => {
    if (
      isSupported &&
      !isLoading &&
      permission === "granted" &&
      !isSubscribed &&
      !isToggling
    ) {
      void subscribe().catch(() => {
        // User can manually toggle if needed
      });
    }
  }, [isSupported, isLoading, permission, isSubscribed, subscribe, isToggling]);

  if (!isSupported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="h-5 w-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Push notifications are not supported on this device.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const handleToggle = async (checked: boolean) => {
    if (isToggling) return;

    setIsToggling(true);
    setUiChecked(checked);

    try {
      if (checked) {
        const success = await subscribe();
        if (!success) {
          setUiChecked(false);
        }
      } else {
        const success = await unsubscribe();
        if (!success) {
          setUiChecked(true);
        }
      }
    } catch {
      setUiChecked(!checked);
    } finally {
      setIsToggling(false);
    }
  };

  const isDisabled = permission === "denied" || isToggling;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isToggling ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : uiChecked ? (
            <Bell className="h-5 w-5 text-primary" />
          ) : (
            <BellOff className="h-5 w-5" />
          )}
          Push Notifications
        </CardTitle>
        <CardDescription>
          {uiChecked
            ? "You'll receive real-time notifications for new bookings, appointments, and important updates."
            : permission === "denied"
              ? "Notifications are blocked. Enable them in system settings to receive push notifications."
              : "Get instant notifications for new bookings, appointments, and important updates."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          className={`flex items-center justify-between ${
            isDisabled
              ? "cursor-not-allowed opacity-50"
              : "cursor-pointer hover:bg-accent/50 rounded-lg px-2 py-1 -mx-2 -my-1 transition-colors"
          } select-none`}
          role="button"
          tabIndex={isDisabled ? -1 : 0}
          onClick={() => {
            if (!isDisabled) {
              handleToggle(!uiChecked);
            }
          }}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
              e.preventDefault();
              handleToggle(!uiChecked);
            }
          }}
        >
          <div className="flex-1">
            <Label htmlFor="push-notifications-toggle" className="text-sm font-medium">
              Enable Push Notifications
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              {isToggling
                ? uiChecked
                  ? "Disabling..."
                  : "Enabling..."
                : uiChecked
                  ? "Notifications are enabled"
                  : permission === "denied"
                    ? "Permission denied in system settings"
                    : permission === "granted"
                      ? "Turn on to receive notifications"
                      : "Tap to enable push notifications"}
            </p>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <Switch
              id="push-notifications-toggle"
              checked={uiChecked}
              onCheckedChange={handleToggle}
              disabled={isDisabled}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Native (Capacitor iOS/Android) only — web uses no in-app push toggle. */
export function EnablePushNotifications() {
  if (!isNativePushPlatform()) {
    return null;
  }
  return <EnablePushNotificationsNative />;
}
